import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PublicHeader } from "../components/cod/PublicHeader";
import { Seo } from "../components/cod/Seo";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { onboardingApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Building2, User, Briefcase, CheckCircle2, ArrowRight, ArrowLeft, Phone, Mail, MapPin, ShieldCheck,
} from "lucide-react";

// Shared wizard shell — progress bar + step navigation
function WizardShell({ step, steps, title, subtitle, contactBar, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader/>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Chicago Opportunity Desk · Onboarding</div>
        <h1 className="font-serif text-3xl text-slate-900 mt-1">{title}</h1>
        <div className="cod-rule-red mt-3"/>
        {subtitle && <p className="text-slate-600 mt-3 max-w-2xl">{subtitle}</p>}
        {contactBar && (
          <div className="mt-4 rounded-lg bg-white border border-slate-200 p-3 flex flex-wrap gap-4 text-sm text-slate-700">
            <span className="flex items-center gap-2"><Phone size={14}/> <a href="tel:+13123434183" className="cod-link">(312) 343-4183</a></span>
            <span className="flex items-center gap-2"><Mail size={14}/> <a href="mailto:info@joinCOD.org" className="cod-link">info@joinCOD.org</a></span>
            <span className="flex items-center gap-2 text-xs text-slate-500">Prefer to talk to someone? Contact us anytime.</span>
          </div>
        )}
        <div className="mt-8 mb-3 flex items-center gap-2" data-testid="wizard-progress">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded ${i <= step ? "bg-[var(--cod-red)]" : "bg-slate-200"}`}/>
          ))}
        </div>
        <div className="mb-4 text-sm text-slate-600" data-testid="wizard-step-label">Step {step + 1} of {steps.length}: <span className="text-slate-900 font-semibold">{steps[step]}</span></div>
        {children}
      </div>
    </div>
  );
}

function StepNav({ step, total, onBack, onNext, submitLabel, canNext = true, submitting = false, testidPrefix = "onb" }) {
  return (
    <div className="mt-6 flex justify-between items-center gap-2 pt-4 border-t border-slate-100">
      <Button type="button" variant="outline" onClick={onBack} disabled={step === 0 || submitting} data-testid={`${testidPrefix}-back`}><ArrowLeft size={14} className="mr-1"/>Back</Button>
      <Button type="submit" className="cod-bg-red" disabled={!canNext || submitting} data-testid={`${testidPrefix}-next`}>
        {submitting ? "Submitting…" : step === total - 1 ? (submitLabel || "Complete") : (<>Next <ArrowRight size={14} className="ml-1"/></>)}
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EMPLOYER SELF-ONBOARDING WIZARD
// ═══════════════════════════════════════════════════════════════════════════
const EMP_STEPS = ["Company", "Contact & Account", "Hiring Profile", "First Job Order (optional)"];

export function EmployerOnboarding() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    organization_name: "", industry: "", website: "", address: "", employee_count: "",
    contact_full_name: "", contact_title: "", email: "", phone: "", password: "",
    typical_positions: "", typical_headcount_per_year: "",
    hiring_needs: "", workforce_challenges: "",
    certifications_typically_required: "",
    post_first_job: false, first_job_role_title: "", first_job_headcount: 1,
    first_job_wage_min: "", first_job_wage_max: "", first_job_wage_unit: "hour",
    first_job_location: "", first_job_description: "", first_job_priority: "medium",
    heard_about_us: "", agree_to_terms: false,
  });
  const upd = (k) => (v) => setF({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v });
  const canStep = () => {
    if (step === 0) return f.organization_name.trim().length > 1;
    if (step === 1) return f.contact_full_name.trim() && f.email.includes("@") && f.password.length >= 8;
    if (step === 3) return f.agree_to_terms;
    return true;
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        organization_name: f.organization_name.trim(),
        industry: f.industry || null,
        website: f.website || null,
        address: f.address || null,
        employee_count: f.employee_count || null,
        contact_full_name: f.contact_full_name.trim(),
        contact_title: f.contact_title || null,
        email: f.email.trim(),
        phone: f.phone || null,
        password: f.password,
        typical_positions: (f.typical_positions || "").split(",").map(s=>s.trim()).filter(Boolean),
        typical_headcount_per_year: f.typical_headcount_per_year ? parseInt(f.typical_headcount_per_year, 10) : null,
        hiring_needs: f.hiring_needs || null,
        workforce_challenges: f.workforce_challenges || null,
        certifications_typically_required: (f.certifications_typically_required || "").split(",").map(s=>s.trim()).filter(Boolean),
        heard_about_us: f.heard_about_us || null,
        agree_to_terms: !!f.agree_to_terms,
        post_first_job: !!f.post_first_job,
      };
      if (f.post_first_job && f.first_job_role_title) {
        payload.first_job = {
          role_title: f.first_job_role_title,
          headcount: parseInt(f.first_job_headcount || 1, 10),
          wage_min: f.first_job_wage_min ? parseFloat(f.first_job_wage_min) : null,
          wage_max: f.first_job_wage_max ? parseFloat(f.first_job_wage_max) : null,
          wage_unit: f.first_job_wage_unit || "hour",
          location: f.first_job_location || null,
          description: f.first_job_description || null,
          priority: f.first_job_priority || "medium",
          status: "open",
        };
      }
      const res = await onboardingApi.employer(payload);
      setUser(res.user);
      toast.success("Welcome to Chicago Opportunity Desk!");
      navigate(res.job_order_id
        ? `/portal/employer/job-orders/${res.job_order_id}`
        : "/portal/employer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed — please try again");
    } finally { setSubmitting(false); }
  };
  const onNext = (e) => {
    e.preventDefault();
    if (!canStep()) return;
    if (step < EMP_STEPS.length - 1) setStep(step + 1);
    else submit();
  };

  return (
    <>
      <Seo title="Employer Onboarding" description="Join Chicago Opportunity Desk as a hiring partner. Post job orders, receive pre-vetted candidates, and track retention." path="/onboarding/employer"/>
      <WizardShell step={step} steps={EMP_STEPS} title="Become a COD hiring partner"
        subtitle="Complete this 4-step onboarding — no phone call required. We&#39;ll get your organization set up and post your first job order in about 5 minutes."
        contactBar>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <form onSubmit={onNext} className="space-y-4">
              {step === 0 && (
                <div className="space-y-4" data-testid="emp-onb-step-0">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><Building2 size={20} className="text-[var(--cod-navy)]"/> Company</div>
                  <div><Label>Organization name *</Label><Input required value={f.organization_name} onChange={upd("organization_name")} data-testid="emp-org-name" placeholder="e.g., Loop Logistics Inc."/></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Industry</Label><Input value={f.industry} onChange={upd("industry")} placeholder="Warehousing" data-testid="emp-industry"/></div>
                    <div>
                      <Label>Employee count</Label>
                      <Select value={f.employee_count} onValueChange={upd("employee_count")}>
                        <SelectTrigger data-testid="emp-employee-count"><SelectValue placeholder="Select range"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-10">1–10</SelectItem>
                          <SelectItem value="11-50">11–50</SelectItem>
                          <SelectItem value="51-200">51–200</SelectItem>
                          <SelectItem value="201-500">201–500</SelectItem>
                          <SelectItem value="500+">500+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Website</Label><Input value={f.website} onChange={upd("website")} placeholder="https://…" data-testid="emp-website"/></div>
                  <div><Label>Address <span className="text-slate-400 text-xs">(optional)</span></Label><Input value={f.address} onChange={upd("address")} placeholder="City, State" data-testid="emp-address"/></div>
                </div>
              )}
              {step === 1 && (
                <div className="space-y-4" data-testid="emp-onb-step-1">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><User size={20} className="text-[var(--cod-navy)]"/> Primary contact & account</div>
                  <div className="text-sm text-slate-600">You&#39;ll use this email + password to sign in to your Employer Portal.</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Full name *</Label><Input required value={f.contact_full_name} onChange={upd("contact_full_name")} data-testid="emp-contact-name"/></div>
                    <div><Label>Title</Label><Input value={f.contact_title} onChange={upd("contact_title")} placeholder="HR Director" data-testid="emp-contact-title"/></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Email *</Label><Input required type="email" value={f.email} onChange={upd("email")} data-testid="emp-email"/></div>
                    <div><Label>Phone</Label><Input value={f.phone} onChange={upd("phone")} placeholder="+1 (312)…" data-testid="emp-phone"/></div>
                  </div>
                  <div>
                    <Label>Password * <span className="text-slate-400 text-xs">(at least 8 chars, mixed case + number)</span></Label>
                    <Input required type="password" value={f.password} onChange={upd("password")} data-testid="emp-password"/>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4" data-testid="emp-onb-step-2">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><Briefcase size={20} className="text-[var(--cod-navy)]"/> Hiring profile</div>
                  <div className="text-sm text-slate-600">Tell us how you typically hire so we can proactively match candidates.</div>
                  <div><Label>Positions you typically hire (comma-separated)</Label><Input value={f.typical_positions} onChange={upd("typical_positions")} placeholder="Warehouse Associate, Forklift Operator, CNA" data-testid="emp-typical-positions"/></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Typical headcount / year</Label><Input type="number" min="0" value={f.typical_headcount_per_year} onChange={upd("typical_headcount_per_year")} data-testid="emp-typical-hc"/></div>
                    <div><Label>Certifications you require (comma-separated)</Label><Input value={f.certifications_typically_required} onChange={upd("certifications_typically_required")} placeholder="OSHA-10, CDL-A" data-testid="emp-typical-certs"/></div>
                  </div>
                  <div><Label>Current hiring needs</Label><textarea rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={f.hiring_needs} onChange={upd("hiring_needs")} placeholder="e.g., 15 warehouse associates for Q2, 2nd shift" data-testid="emp-hiring-needs"/></div>
                  <div><Label>Workforce challenges</Label><textarea rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={f.workforce_challenges} onChange={upd("workforce_challenges")} placeholder="e.g., high turnover, difficulty finding certified CNAs" data-testid="emp-workforce-challenges"/></div>
                  <div><Label>How did you hear about us?</Label><Input value={f.heard_about_us} onChange={upd("heard_about_us")} placeholder="Google, referral, event…" data-testid="emp-heard"/></div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-4" data-testid="emp-onb-step-3">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><Briefcase size={20} className="text-[var(--cod-navy)]"/> Post your first job order (optional)</div>
                  <div className="text-sm text-slate-600">You can post an immediate job order or skip and post later from your portal.</div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={f.post_first_job} onCheckedChange={v => setF({ ...f, post_first_job: !!v })} data-testid="emp-post-first-job"/>
                    <span>Yes, I&#39;d like to post my first job order now</span>
                  </label>
                  {f.post_first_job && (
                    <div className="space-y-3 pl-6 border-l-2 border-[var(--cod-red)]">
                      <div><Label>Role title *</Label><Input value={f.first_job_role_title} onChange={upd("first_job_role_title")} placeholder="Warehouse Associate" data-testid="emp-fj-role"/></div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label>Headcount</Label><Input type="number" min="1" value={f.first_job_headcount} onChange={upd("first_job_headcount")} data-testid="emp-fj-headcount"/></div>
                        <div><Label>Wage min ($)</Label><Input type="number" step="0.01" value={f.first_job_wage_min} onChange={upd("first_job_wage_min")} data-testid="emp-fj-wmin"/></div>
                        <div><Label>Wage max ($)</Label><Input type="number" step="0.01" value={f.first_job_wage_max} onChange={upd("first_job_wage_max")} data-testid="emp-fj-wmax"/></div>
                      </div>
                      <div><Label>Location</Label><Input value={f.first_job_location} onChange={upd("first_job_location")} placeholder="Chicago, IL" data-testid="emp-fj-location"/></div>
                      <div><Label>Description</Label><textarea rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={f.first_job_description} onChange={upd("first_job_description")} data-testid="emp-fj-desc"/></div>
                    </div>
                  )}
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <Checkbox checked={f.agree_to_terms} onCheckedChange={v => setF({ ...f, agree_to_terms: !!v })} data-testid="emp-agree"/>
                      <span>I agree to Chicago Opportunity Desk&#39;s <Link to="/legal/terms" className="cod-link">Employer Terms of Service</Link> and <Link to="/legal/privacy" className="cod-link">Privacy Policy</Link>. I understand there are no placement fees.</span>
                    </label>
                  </div>
                </div>
              )}
              <StepNav step={step} total={EMP_STEPS.length}
                onBack={()=>setStep(Math.max(0, step-1))}
                onNext={onNext}
                canNext={canStep()}
                submitting={submitting}
                submitLabel="Create account & continue"
                testidPrefix="emp-onb"/>
            </form>
          </CardContent>
        </Card>
        <div className="mt-6 text-xs text-slate-500 text-center">
          Already have an account? <Link to="/signin" className="cod-link">Sign in</Link>
        </div>
      </WizardShell>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PARTNER SELF-ONBOARDING WIZARD
// ═══════════════════════════════════════════════════════════════════════════
const PARTNER_STEPS = ["Organization Type", "Programs & Credentials", "Availability & Reach", "Contact & Account"];
const PARTNER_TYPE_LABELS = {
  community_partner: "Community-Based Organization",
  training_provider: "Training / Education Provider",
  government_agency: "Government Agency",
  funding_org: "Funder / Foundation",
};

export function PartnerOnboarding() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initType = ["community_partner","training_provider","government_agency","funding_org"]
    .includes(params.get("type")) ? params.get("type") : "community_partner";
  const [f, setF] = useState({
    organization_name: "", partner_type: initType, also_hires: false,
    website: "", address: "",
    programs_offered: "", credentials_awarded: "", instructor_certifications: "",
    populations_served: "", availability: "", pricing_model: "",
    typical_cost: "", referral_capacity_per_month: "",
    contact_full_name: "", contact_title: "", email: "", phone: "", password: "",
    heard_about_us: "", agree_to_terms: false,
  });
  // Re-hydrate partner_type if query changes
  useEffect(() => {
    const t = params.get("type");
    if (t && ["community_partner","training_provider","government_agency","funding_org"].includes(t)) {
      setF(prev => ({ ...prev, partner_type: t }));
    }
  }, [params]);
  const upd = (k) => (v) => setF({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v });
  const canStep = () => {
    if (step === 0) return f.organization_name.trim().length > 1;
    if (step === 3) return f.contact_full_name.trim() && f.email.includes("@") && f.password.length >= 8 && f.agree_to_terms;
    return true;
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        organization_name: f.organization_name.trim(),
        partner_type: f.partner_type,
        also_hires: !!f.also_hires,
        website: f.website || null,
        address: f.address || null,
        contact_full_name: f.contact_full_name.trim(),
        contact_title: f.contact_title || null,
        email: f.email.trim(),
        phone: f.phone || null,
        password: f.password,
        programs_offered: (f.programs_offered || "").split(",").map(s=>s.trim()).filter(Boolean),
        credentials_awarded: (f.credentials_awarded || "").split(",").map(s=>s.trim()).filter(Boolean),
        instructor_certifications: (f.instructor_certifications || "").split(",").map(s=>s.trim()).filter(Boolean),
        populations_served: (f.populations_served || "").split(",").map(s=>s.trim()).filter(Boolean),
        availability: f.availability || null,
        pricing_model: f.pricing_model || null,
        typical_cost: f.typical_cost || null,
        referral_capacity_per_month: f.referral_capacity_per_month ? parseInt(f.referral_capacity_per_month, 10) : null,
        heard_about_us: f.heard_about_us || null,
        agree_to_terms: !!f.agree_to_terms,
      };
      const res = await onboardingApi.partner(payload);
      setUser(res.user);
      toast.success("Welcome to Chicago Opportunity Desk!");
      // Route based on partner type
      const routeByRole = {
        community_partner: "/partner",
        training_provider: "/portal/training",
        government_agency: "/portal/government",
        funding_org: "/portal/funding",
      };
      navigate(routeByRole[f.partner_type] || "/access");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed — please try again");
    } finally { setSubmitting(false); }
  };
  const onNext = (e) => {
    e.preventDefault();
    if (!canStep()) return;
    if (step < PARTNER_STEPS.length - 1) setStep(step + 1);
    else submit();
  };
  return (
    <>
      <Seo title="Partner Onboarding" description="Join Chicago Opportunity Desk as a community partner, training provider, government agency, or funder. Set up your organization profile in minutes." path="/onboarding/partner"/>
      <WizardShell step={step} steps={PARTNER_STEPS} title="Become a COD partner"
        subtitle="Community organizations, training providers, government agencies, and funders — set up your organization profile in about 5 minutes. No phone call required."
        contactBar>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <form onSubmit={onNext} className="space-y-4">
              {step === 0 && (
                <div className="space-y-4" data-testid="par-onb-step-0">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><Building2 size={20} className="text-[var(--cod-navy)]"/> Organization type</div>
                  <div><Label>Organization name *</Label><Input required value={f.organization_name} onChange={upd("organization_name")} data-testid="par-org-name" placeholder="e.g., HealthPath Training Institute"/></div>
                  <div>
                    <Label>What type of organization are you?</Label>
                    <div className="grid sm:grid-cols-2 gap-2 mt-1" data-testid="par-type-select">
                      {Object.entries(PARTNER_TYPE_LABELS).map(([v, l]) => (
                        <label key={v} className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer ${f.partner_type === v ? "border-[var(--cod-red)] bg-[var(--cod-red)]/5" : "border-slate-200 hover:border-slate-400"}`}>
                          <input type="radio" name="partner_type" value={v} checked={f.partner_type === v} onChange={()=>setF({...f, partner_type: v})} data-testid={`par-type-${v}`}/>
                          <span className="text-sm text-slate-900 font-medium">{l}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={f.also_hires} onCheckedChange={v => setF({ ...f, also_hires: !!v })} data-testid="par-also-hires"/>
                    <span>My organization also directly hires (multi-classification: partner + employer)</span>
                  </label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Website</Label><Input value={f.website} onChange={upd("website")} placeholder="https://…" data-testid="par-website"/></div>
                    <div><Label>Location / address</Label><Input value={f.address} onChange={upd("address")} placeholder="City, State" data-testid="par-address"/></div>
                  </div>
                </div>
              )}
              {step === 1 && (
                <div className="space-y-4" data-testid="par-onb-step-1">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><ShieldCheck size={20} className="text-[var(--cod-navy)]"/> What you teach & who you certify</div>
                  <div><Label>Programs you offer (comma-separated)</Label><Input value={f.programs_offered} onChange={upd("programs_offered")} placeholder="CNA Training, CPR/BLS, Financial Literacy" data-testid="par-programs"/></div>
                  <div><Label>Credentials you award / issue upon completion</Label><Input value={f.credentials_awarded} onChange={upd("credentials_awarded")} placeholder="CNA, CPR Certified, ServSafe" data-testid="par-credentials"/></div>
                  <div><Label>Certifications your instructors hold</Label><Input value={f.instructor_certifications} onChange={upd("instructor_certifications")} placeholder="RN, LPN, CPR Instructor" data-testid="par-instructor-certs"/></div>
                  <div><Label>Populations served</Label><Input value={f.populations_served} onChange={upd("populations_served")} placeholder="Returning citizens, Youth 18-24, Single parents" data-testid="par-populations"/></div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4" data-testid="par-onb-step-2">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><MapPin size={20} className="text-[var(--cod-navy)]"/> Availability & reach</div>
                  <div><Label>Availability / hours of operation</Label><textarea rows={2} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={f.availability} onChange={upd("availability")} placeholder="e.g., Mon-Fri 9am-5pm, evening cohorts available" data-testid="par-availability"/></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Pricing model</Label>
                      <Select value={f.pricing_model} onValueChange={upd("pricing_model")}>
                        <SelectTrigger data-testid="par-pricing-model"><SelectValue placeholder="Select…"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Free to participants">Free to participants</SelectItem>
                          <SelectItem value="Sliding scale">Sliding scale</SelectItem>
                          <SelectItem value="Grant-funded">Grant-funded</SelectItem>
                          <SelectItem value="Fee-for-service">Fee-for-service</SelectItem>
                          <SelectItem value="Employer-sponsored">Employer-sponsored</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Typical cost (per participant)</Label><Input value={f.typical_cost} onChange={upd("typical_cost")} placeholder="$0 · $500 · Varies by cohort" data-testid="par-cost"/></div>
                  </div>
                  <div><Label>Referrals you can accept per month</Label><Input type="number" min="0" value={f.referral_capacity_per_month} onChange={upd("referral_capacity_per_month")} placeholder="e.g., 25" data-testid="par-referral-capacity"/></div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-4" data-testid="par-onb-step-3">
                  <div className="flex items-center gap-2 text-lg font-serif text-slate-900"><User size={20} className="text-[var(--cod-navy)]"/> Contact & account</div>
                  <div className="text-sm text-slate-600">You&#39;ll use this email + password to sign in to your Partner Portal.</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Full name *</Label><Input required value={f.contact_full_name} onChange={upd("contact_full_name")} data-testid="par-contact-name"/></div>
                    <div><Label>Title / Role</Label><Input value={f.contact_title} onChange={upd("contact_title")} placeholder="Executive Director" data-testid="par-contact-title"/></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Email *</Label><Input required type="email" value={f.email} onChange={upd("email")} data-testid="par-email"/></div>
                    <div><Label>Phone</Label><Input value={f.phone} onChange={upd("phone")} placeholder="+1 (312)…" data-testid="par-phone"/></div>
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <Input required type="password" value={f.password} onChange={upd("password")} data-testid="par-password"/>
                    <div className="text-[11px] text-slate-500 mt-1">At least 8 chars, mixed case + number.</div>
                  </div>
                  <div><Label>How did you hear about us?</Label><Input value={f.heard_about_us} onChange={upd("heard_about_us")} placeholder="Referral, event, search…" data-testid="par-heard"/></div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <Checkbox checked={f.agree_to_terms} onCheckedChange={v => setF({ ...f, agree_to_terms: !!v })} data-testid="par-agree"/>
                      <span>I agree to Chicago Opportunity Desk&#39;s <Link to="/legal/terms" className="cod-link">Partner Terms of Service</Link> and <Link to="/legal/privacy" className="cod-link">Privacy Policy</Link>.</span>
                    </label>
                  </div>
                </div>
              )}
              <StepNav step={step} total={PARTNER_STEPS.length}
                onBack={()=>setStep(Math.max(0, step-1))}
                onNext={onNext}
                canNext={canStep()}
                submitting={submitting}
                submitLabel="Create account & continue"
                testidPrefix="par-onb"/>
            </form>
          </CardContent>
        </Card>
        <div className="mt-6 text-xs text-slate-500 text-center">
          Already have an account? <Link to="/signin" className="cod-link">Sign in</Link>
        </div>
      </WizardShell>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ONBOARDING HUB — landing page directing to the right wizard
// ═══════════════════════════════════════════════════════════════════════════
export function OnboardingHub() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Seo title="Join Chicago Opportunity Desk" description="Register as an employer or partner organization with Chicago Opportunity Desk." path="/onboarding"/>
      <PublicHeader/>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Chicago Opportunity Desk</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-2">Set up your organization</h1>
        <div className="cod-rule-red mt-4 mx-auto"/>
        <p className="text-slate-600 mt-4 max-w-xl mx-auto">Self-serve onboarding — no phone call required. Complete your profile in about 5 minutes and land directly in your portal.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-10 text-left">
          <Link to="/onboarding/employer" data-testid="onb-choose-employer" className="rounded-lg border-2 border-slate-200 bg-white p-6 hover:border-[var(--cod-red)] transition-colors">
            <Briefcase size={28} className="text-[var(--cod-navy)]"/>
            <h2 className="font-serif text-xl text-slate-900 mt-3">I&#39;m an Employer</h2>
            <p className="text-sm text-slate-600 mt-2">Post job orders, receive pre-vetted candidates, and track retention. No placement fees.</p>
            <div className="mt-4 text-sm font-semibold text-[var(--cod-red)] flex items-center gap-1">Start employer onboarding <ArrowRight size={14}/></div>
          </Link>
          <Link to="/onboarding/partner" data-testid="onb-choose-partner" className="rounded-lg border-2 border-slate-200 bg-white p-6 hover:border-[var(--cod-red)] transition-colors">
            <CheckCircle2 size={28} className="text-[var(--cod-navy)]"/>
            <h2 className="font-serif text-xl text-slate-900 mt-3">I&#39;m a Partner Organization</h2>
            <p className="text-sm text-slate-600 mt-2">Community organizations, training providers, government agencies, and funders — bidirectional referrals and shared outcomes.</p>
            <div className="mt-4 text-sm font-semibold text-[var(--cod-red)] flex items-center gap-1">Start partner onboarding <ArrowRight size={14}/></div>
          </Link>
        </div>
        <div className="mt-10 text-sm text-slate-500">
          Already have an account? <Link to="/signin" className="cod-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
